import React, { useState, useEffect } from 'react';
import { CheckCircle2, Circle, Users, TrendingUp, DollarSign, BarChart3, Target, ShoppingCart, Settings, Brain, PieChart, ChevronDown, ChevronUp, Search, Map, Zap, Download, X, Loader2, Activity, Heart, Stethoscope, Hospital, Pill, UserCheck } from 'lucide-react';
import { BedrockAgentRuntimeClient, InvokeAgentCommand } from "@aws-sdk/client-bedrock-agent-runtime";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-providers";
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { SignatureV4 } from "@aws-sdk/signature-v4";
import { HttpRequest } from "@aws-sdk/protocol-http";
import { Sha256 } from "@aws-crypto/sha256-browser";
import { v4 as uuidv4 } from 'uuid';
import * as XLSX from 'xlsx';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ChartWithControls from '../chatbotui/chartwithcontrols';
import DataTable from './datatable';
import SQLValidator from '../../utils/SQLValidator';
import { buildSQLPrompt } from '../../utils/prompts';
import config from '../../amplifyconfiguration.json';

const GuiaInteractiva = () => {
    const [activeTab, setActiveTab] = useState('fases');
    const [completedSteps, setCompletedSteps] = useState(new Set());
    const [expandedSections, setExpandedSections] = useState(new Set([0]));
    
    // Estados para el modal y agente
    const [selectedQuery, setSelectedQuery] = useState(null);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [agentResponse, setAgentResponse] = useState('');
    const [vizLoading, setVizLoading] = useState(false);
    const [queryData, setQueryData] = useState(null);
    const [currentMessageId, setCurrentMessageId] = useState(null);

    // Estado para el sistema de cache (usando objeto simple)
    const [queryCache, setQueryCache] = useState({});

    // Usuario y configuración
    const { user } = useAuthenticator((context) => [context.user]);
    const id_user = user?.username || 'demo_user';
    const id_conversation = `guia_interactiva_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // URLs y configuración (usando las mismas variables que el chatbot original)
    const baseURL = process.env.REACT_APP_BASE_URL;
    const baseURLBD = process.env.REACT_APP_BD;
    const AGENT_ID = "2UMMTYX5GF";
    const AGENT_ALIAS_ID = "3M1MQMTGPR";
    
    // Endpoints (igual que en el chatbot original)
    const endpoint_add_message = '/add_message';
    const endpoint_get_sql = '/get_query';
    const endpoint_executeSQL = '/get_rds';

    // Funciones de cache (versión simplificada)
    const generateCacheKey = (query) => {
        return query.toLowerCase().trim().replace(/[^a-z0-9]/g, '_');
    };

    const saveToCache = (query, data) => {
        const cacheKey = generateCacheKey(query);
        setQueryCache(prev => ({
            ...prev,
            [cacheKey]: {
                ...data,
                timestamp: Date.now(),
                query: query
            }
        }));
    };

    const getFromCache = (query) => {
        const cacheKey = generateCacheKey(query);
        return queryCache[cacheKey];
    };

    const existsInCache = (query) => {
        const cacheKey = generateCacheKey(query);
        return !!queryCache[cacheKey];
    };

    const roleIcons = {
        "Director Médico": Stethoscope,
        "Director Financiero": DollarSign,
        "Gerente de Operaciones": Settings,
        "Coordinador de Especialidades": Heart,
        "Jefe de Enfermería": UserCheck,
        "Administrador Hospitalario": Hospital,
        "Analista de Calidad": BarChart3,
        "Coordinador de Recursos Humanos": Users,
        "Responsable de Facturación": Pill
    };

    const faseIcons = {
        "Exploración de Datos Médicos (EDA)": Search,
        "Análisis de Productividad Hospitalaria": BarChart3,
        "Analítica Avanzada de Especialidades": Brain,
        "Análisis de Centros Sanitarios": Hospital,
        "Optimización de Recursos Médicos": Zap,
        "Reportes Ejecutivos de Salud": Download
    };

    const consultasPorRol = [
        {
            title: "Director Médico",
            description: "Supervisión clínica y calidad asistencial",
            priority: "Alta",
            timeEstimate: "15-20 min",
            consultas: [
                "¿Cuál es la productividad por especialidad médica en el último trimestre?",
                "Análisis de calidad asistencial: estudios por médico y especialidad",
                "¿Qué especialidades tienen mayor volumen de procedimientos?",
                "Tendencias de actividad médica por centro sanitario"
            ]
        },
        {
            title: "Director Financiero",
            description: "Análisis financiero y rentabilidad hospitalaria",
            priority: "Alta",
            timeEstimate: "18-25 min",
            consultas: [
                "¿Cuál es la facturación por especialidad y centro sanitario?",
                "Análisis de rentabilidad por tipo de paciente (externos vs internos)",
                "¿Qué médicos generan mayor facturación por especialidad?",
                "Tendencias de ingresos hospitalarios en los últimos 12 meses"
            ]
        },
        {
            title: "Gerente de Operaciones",
            description: "Eficiencia operativa y gestión de recursos",
            priority: "Alta",
            timeEstimate: "20-25 min",
            consultas: [
                "¿Cuál es la distribución de carga de trabajo por médico?",
                "Análisis de eficiencia operativa por centro sanitario",
                "¿Qué centros tienen mayor volumen de estudios realizados?",
                "Identificar cuellos de botella en la atención médica"
            ]
        },
        {
            title: "Coordinador de Especialidades",
            description: "Gestión y optimización de especialidades médicas",
            priority: "Media",
            timeEstimate: "15-20 min",
            consultas: [
                "¿Cuáles son las especialidades con mayor demanda?",
                "Análisis comparativo de productividad entre especialidades",
                "¿Qué especialidades requieren más recursos o personal?",
                "Tendencias de crecimiento por especialidad médica"
            ]
        }
    ];

    const consultasPorFase = [
        {
            title: "Exploración de Datos Médicos (EDA)",
            description: "Análisis exploratorio de datos hospitalarios para entender patrones de atención médica",
            priority: "Alta",
            timeEstimate: "20-25 min",
            consultas: [
                {
                    text: "¿Cuál es la distribución de estudios realizados por especialidad médica en 2024?",
                },
                {
                    text: "Muestra la evolución mensual de la actividad médica por centro sanitario",
                },
                {
                    text: "¿Qué médicos tienen mayor productividad por especialidad?",
                },
                {
                    text: "Análisis de patrones de facturación por tipo de paciente",
                },
                {
                    text: "¿Cuál es la correlación entre estudios realizados y facturación por médico?",
                }
            ]
        },
        {
            title: "Análisis de Productividad Hospitalaria",
            description: "Métricas clave de productividad médica y eficiencia operativa",
            priority: "Alta",
            timeEstimate: "18-22 min",
            consultas: [
                {
                    text: "¿Cuál es el ranking de productividad por médico en cada especialidad?",
                },
                {
                    text: "Análisis comparativo de facturación entre centros sanitarios",
                },
                {
                    text: "¿Qué especialidades tienen mejor ratio estudios/facturación?",
                }
            ]
        },
        {
            title: "Analítica Avanzada de Especialidades",
            description: "Análisis predictivo y de tendencias para optimización de especialidades médicas",
            priority: "Alta",
            timeEstimate: "25-30 min",
            consultas: [
                {
                    text: "¿Cuáles son las tendencias de crecimiento por especialidad médica?",
                },
                {
                    text: "Simula escenarios: ¿qué pasa si se aumenta 20% la capacidad en cardiología?",
                },
                {
                    text: "¿Cuál sería el impacto de redistribuir médicos entre centros sanitarios?",
                }
            ]
        },
        {
            title: "Análisis de Centros Sanitarios",
            description: "Métricas de performance y comparación entre diferentes centros de salud",
            priority: "Media",
            timeEstimate: "15-20 min",
            consultas: [
                {
                    text: "¿Cómo varía la productividad médica entre diferentes centros sanitarios?",
                },
                {
                    text: "Identifica oportunidades de mejora por centro de salud",
                }
            ]
        },
        {
            title: "Optimización de Recursos Médicos",
            description: "Recomendaciones inteligentes para optimización de recursos hospitalarios",
            priority: "Alta",
            timeEstimate: "20-25 min",
            consultas: [
                {
                    text: "Recomienda redistribución óptima de médicos por especialidad",
                },
                {
                    text: "¿Cuál es la estrategia ideal de recursos para Q1 2025?",
                }
            ]
        },
        {
            title: "Reportes Ejecutivos de Salud",
            description: "Exportación de reportes ejecutivos y KPIs del sector salud",
            priority: "Media",
            timeEstimate: "10-15 min",
            consultas: [
                {
                    text: "Dame un reporte ejecutivo de productividad por especialidad",
                },
                {
                    text: "Exporta KPIs de calidad asistencial por centro sanitario",
                }
            ]
        }
    ];

    // [Mantener todas las funciones originales sin cambios]
    const createAuthenticatedRequest = async (config) => {
        const authToken = (await fetchAuthSession()).tokens?.idToken?.toString();
        const my_login = 'cognito-idp.' + config.aws_project_region + '.amazonaws.com/' + config.aws_user_pools_id;

        const credentials = fromCognitoIdentityPool({
            clientConfig: { region: config.aws_project_region },
            identityPoolId: config.aws_cognito_identity_pool_id,
            logins: { [my_login]: authToken }
        });

        const signer = new SignatureV4({
            credentials: credentials,
            region: config.aws_project_region,
            service: 'execute-api',
            sha256: Sha256
        });

        return async (endpoint, method = 'GET', body = null) => {
            const url = new URL(endpoint);

            const request = new HttpRequest({
                hostname: url.hostname,
                path: url.pathname + url.search,
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    host: url.hostname
                },
                body: body ? JSON.stringify(body) : undefined
            });

            const signedRequest = await signer.sign(request);
            const fetchOptions = {
                method: signedRequest.method,
                headers: signedRequest.headers,
                body: signedRequest.body
            };

            const response = await fetch(url.toString(), fetchOptions);
            return response.json();
        };
    };

    const invokeBedrockAgent = async (prompt, sessionId, id_message_bot) => {
        const authToken = (await fetchAuthSession()).tokens?.idToken?.toString();
        const my_login = 'cognito-idp.' + config.aws_project_region + '.amazonaws.com/' + config.aws_user_pools_id;
        
        const bedrock = new BedrockAgentRuntimeClient({
            region: config.aws_project_region,
            credentials: fromCognitoIdentityPool({
                clientConfig: { region: config.aws_project_region },
                identityPoolId: config.aws_cognito_identity_pool_id,
                logins: { [my_login]: authToken }
            })
        });

        const input = {
            agentId: AGENT_ID,
            agentAliasId: AGENT_ALIAS_ID,
            sessionId,
            inputText: prompt,
            enableTrace: true,
            sessionState: {
                sessionAttributes: {
                    id_message: id_message_bot
                }
            }
        };

        const command = new InvokeAgentCommand(input);
        let completion = "";

        const response = await bedrock.send(command);
        if (response.completion === undefined) {
            throw new Error("Completion is undefined");
        }

        for await (let chunkEvent of response.completion) {
            if (chunkEvent.chunk) {
                const chunk = chunkEvent.chunk;
                const decodedResponse = new TextDecoder("utf-8").decode(chunk.bytes);
                completion += decodedResponse;
            }
        }

        return completion;
    };

    const addMessageBD = async (id_conversation, id_user, message, author, id_message) => {
        try {
            const authenticatedRequest = await createAuthenticatedRequest(config);
            const url = `${baseURL}${endpoint_add_message}`;

            const response = await authenticatedRequest(
                url,
                'POST',
                {
                    id_user: id_user,
                    id_conversation: id_conversation,
                    content: message,
                    author: author,
                    id_message: id_message
                }
            );

            return response.id_message;
        } catch (error) {
            console.error('Error adding message to BD:', error);
            return "None";
        }
    };

    const getQuery = async (id_message) => {
        try {
            const authenticatedRequest = await createAuthenticatedRequest(config);
            const url = `${baseURL}${endpoint_get_sql}?id_message=${encodeURIComponent(id_message)}`;
            const response = await authenticatedRequest(url, 'GET', null);
            return response.messages;
        } catch (error) {
            console.error('Error getting query:', error);
            return [];
        }
    };

    const sendMessageToClaude = async (mensajes, userContent) => {
        const apiKey = "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";
        const mensajes_form = mensajes.map(({ question, query }) => ({ question, query }));
        
        const safeJSONStringify = (obj) => {
            try {
                return JSON.stringify(obj);
            } catch (error) {
                console.warn("Error en serialización JSON:", error);
                const seen = new WeakSet();
                return JSON.stringify(obj, (key, value) => {
                    if (typeof value === 'object' && value !== null) {
                        if (seen.has(value)) {
                            return "[Referencia Circular]";
                        }
                        seen.add(value);
                    }
                    if (typeof value === 'function') {
                        return "[Función]";
                    }
                    if (value instanceof Error) {
                        return {
                            errorType: value.name,
                            errorMessage: value.message
                        };
                    }
                    return value;
                }, 2);
            }
        };

        const content = buildSQLPrompt(userContent, safeJSONStringify(mensajes_form));

        return fetch("https://api.anthropic.com/v1/messages", {
            method: "POST",
            headers: {
                "x-api-key": apiKey,
                "anthropic-version": "2023-06-01",
                "content-type": "application/json",
                "anthropic-dangerous-direct-browser-access": "true"
            },
            body: JSON.stringify({
                model: "claude-sonnet-4-20250514",
                max_tokens: 1000,
                messages: [{
                    role: "user",
                    content: content
                }]
            })
        })
        .then(response => {
            if (!response.ok) {
                console.error('Error en la API de Claude:', response.statusText);
                return null;
            }
            return response.json();
        })
        .catch(error => {
            console.error('Error haciendo la petición:', error);
            return null;
        });
    };

    const executeSQL = async (sqlStatement) => {
        try {
            const url = `${baseURLBD}${endpoint_executeSQL}?sqlStatement=${encodeURIComponent(sqlStatement)}`;
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000);

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error executing SQL query:', error);
            let errorMessage = "Error al ejecutar la consulta SQL";

            if (error.name === 'AbortError') {
                errorMessage = "La consulta ha excedido el tiempo máximo de ejecución";
            } else if (error.message) {
                errorMessage = error.message;
            }

            return {
                data: [],
                columns: {},
                query: sqlStatement,
                error: errorMessage
            };
        }
    };

    const updateMessageBD = async (id_message, query_sql) => {
        try {
            const authenticatedRequest = await createAuthenticatedRequest(config);
            const url = `${baseURL}/update_query_sql`;

            const response = await authenticatedRequest(
                url,
                'POST',
                {
                    id_message: id_message,
                    query_sql: query_sql
                }
            );

            return response.message;
        } catch (error) {
            console.error('Error updating query_sql:', error);
            return "Failed to update query_sql";
        }
    };

    const handleGenerateExcel = () => {
        if (!queryData || !queryData.data) {
            console.error('No hay datos disponibles para generar el Excel.');
            return;
        }
        
        const timestamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\..+/, '');
        const worksheet = XLSX.utils.json_to_sheet(queryData.data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Datos');
        XLSX.writeFile(workbook, `reporte_salud_${timestamp}.xlsx`);
    };

    // Función principal para manejar la consulta (CON CACHE)
    const handleQueryClick = async (query) => {
        const queryText = typeof query === 'object' ? query.text : query;
        
        setSelectedQuery(queryText);
        setModalIsOpen(true);
        setLoading(true);
        setAgentResponse('');
        setQueryData(null);
        setVizLoading(false);

        try {
            // Verificar cache
            if (existsInCache(queryText)) {
                console.log('📦 Respuesta encontrada en cache');
                
                const cachedData = getFromCache(queryText);
                setAgentResponse(cachedData.agentResponse);
                setCurrentMessageId(cachedData.messageId);
                
                if (cachedData.queryData) {
                    setQueryData(cachedData.queryData);
                }
                
                setLoading(false);
                return;
            }

            console.log('🔄 Consultando agente...');

            // Generar IDs únicos
            const id_message_client = uuidv4();
            const id_message_bot = uuidv4();
            setCurrentMessageId(id_message_bot);

            // Agregar mensaje del cliente a BD
            await addMessageBD(id_conversation, id_user, queryText, "Client", id_message_client);

            // Invocar el agente de Bedrock
            const response = await invokeBedrockAgent(queryText, id_conversation, id_message_bot);
            
            if (!response || response === "Sorry, I am unable to assist you with this request.") {
                throw new Error("El agente no pudo procesar la consulta");
            }

            setAgentResponse(response);
            
            // Agregar respuesta del bot a BD
            await addMessageBD(id_conversation, id_user, response, "Bot", id_message_bot);

            // Guardar en cache
            saveToCache(queryText, {
                agentResponse: response,
                messageId: id_message_bot,
                queryData: null,
                sqlQuery: null
            });

        } catch (error) {
            console.error('Error processing query:', error);
            setAgentResponse("Lo siento, hubo un error al procesar tu consulta. Por favor, intenta de nuevo.");
        } finally {
            setLoading(false);
        }
    };

    // Función para manejar la visualización (CON CACHE)
    const handleVizualization = async () => {
        if (!currentMessageId) return;

        setVizLoading(true);
        setQueryData(null);

        try {
            // Verificar cache para visualización
            const cachedData = getFromCache(selectedQuery);
            if (cachedData && cachedData.queryData && cachedData.sqlQuery) {
                console.log('📊 Datos de visualización desde cache');
                setQueryData(cachedData.queryData);
                setVizLoading(false);
                return;
            }

            console.log('🔄 Generando nueva visualización...');

            // Obtener consultas anteriores
            const mensajes_anteriores = await getQuery(currentMessageId);
            
            // Generar consulta SQL con Claude
            const respuestaClaude = await sendMessageToClaude(mensajes_anteriores, selectedQuery);
            
            if (!respuestaClaude || !respuestaClaude.content?.[0]?.text) {
                throw new Error("No se pudo generar la consulta SQL");
            }

            let sqlQuery = respuestaClaude.content[0].text;
            
            // Limpiar la consulta SQL
            const validator = new SQLValidator();
            sqlQuery = validator.cleanMarkdown(sqlQuery);
            
            // Actualizar mensaje con la consulta SQL
            await updateMessageBD(currentMessageId, sqlQuery);
            
            // Ejecutar la consulta SQL
            const records = await executeSQL(sqlQuery);
            
            if (records.error) {
                throw new Error(records.error);
            }

            const newQueryData = {
                data: records.data || [],
                columns: records.columns || {},
                query: sqlQuery,
                column_types: records.column_types || {},
                hasError: false
            };

            setQueryData(newQueryData);

            // Actualizar cache
            if (cachedData) {
                saveToCache(selectedQuery, {
                    ...cachedData,
                    queryData: newQueryData,
                    sqlQuery: sqlQuery
                });
            }

        } catch (error) {
            console.error('Error in visualization:', error);
            const errorData = {
                data: [],
                columns: {},
                query: "",
                column_types: {},
                hasError: true,
                error: error.message || "No se puede generar la gráfica. Por favor, intenta reformular tu pregunta."
            };
            
            setQueryData(errorData);
            
            const cachedData = getFromCache(selectedQuery);
            if (cachedData) {
                saveToCache(selectedQuery, {
                    ...cachedData,
                    queryData: errorData,
                    sqlQuery: null
                });
            }
        } finally {
            setVizLoading(false);
        }
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setSelectedQuery(null);
        setAgentResponse('');
        setQueryData(null);
        setCurrentMessageId(null);
        setLoading(false);
        setVizLoading(false);
    };

    const dataToShow = activeTab === 'fases' ? consultasPorFase : consultasPorRol;
    const iconsToUse = activeTab === 'fases' ? faseIcons : roleIcons;

    return (
        <div className="roadmap-container">
            {/* Tabs Header */}
            <div className="tabs-header">
                <div className="tabs-nav">
                    <button
                        className={`tab-button ${activeTab === 'fases' ? 'active' : ''}`}
                        onClick={() => setActiveTab('fases')}
                    >
                        <Activity size={20} />
                        Fases de Análisis
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'roles' ? 'active' : ''}`}
                        onClick={() => setActiveTab('roles')}
                    >
                        <Users size={20} />
                        Roadmap por Roles
                    </button>
                </div>
            </div>

            {/* Header */}
            <div className="header">
                <div className="title-section">
                    <h1>{activeTab === 'fases' ? 'Fases de Análisis IA Sector Salud' : 'Roadmap de Consultas por Roles Médicos'}</h1>
                    <p>{activeTab === 'fases' ? 'Metodología estructurada para análisis de datos hospitalarios' : 'Guía completa por roles para maximizar el potencial de la plataforma de salud'}</p>
                </div>

                <div className="progress-section">
                    <div className="progress-stats">
                        <div className="stat">
                            <span className="stat-number">{completedSteps.size}</span>
                            <span className="stat-label">Completados</span>
                        </div>
                        <div className="stat">
                            <span className="stat-number">{dataToShow.length - completedSteps.size}</span>
                            <span className="stat-label">Pendientes</span>
                        </div>
                        <div className="stat">
                            <span className="stat-number">{Math.round((completedSteps.size / dataToShow.length) * 100)}%</span>
                            <span className="stat-label">Progreso</span>
                        </div>
                    </div>
                    <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${(completedSteps.size / dataToShow.length) * 100}%` }} />
                    </div>
                </div>
            </div>

            <div className="main-content">
                {/* Timeline Roadmap */}
                <div className="timeline-container">
                    <div className="timeline-line" />

                    {dataToShow.map((section, index) => {
                        const IconComponent = iconsToUse[section.title];
                        const isCompleted = completedSteps.has(index);
                        const isExpanded = expandedSections.has(index);

                        return (
                            <div key={index} className={`timeline-item ${isCompleted ? 'completed' : ''}`}>
                                <div className="timeline-node">
                                    <IconComponent size={24} color="white" />
                                </div>

                                <div className="timeline-card">
                                    <div className="card-header">
                                        <div className="role-info">
                                            <div className="role-title-row">
                                                <h3>{section.title}</h3>
                                                <div className="badges">
                                                    <span className={`priority-badge priority-${section.priority.toLowerCase()}`}>
                                                        {section.priority}
                                                    </span>
                                                    <span className="time-badge">{section.timeEstimate}</span>
                                                </div>
                                            </div>
                                            <p className="role-description">{section.description}</p>
                                            <div className="role-stats">
                                                <span className="query-count">{section.consultas.length} consultas disponibles</span>
                                            </div>
                                        </div>

                                        <div className="card-actions">
                                            <button
                                                className={`completion-btn ${isCompleted ? 'completed' : ''}`}
                                                onClick={() => {
                                                    const newCompleted = new Set(completedSteps);
                                                    if (newCompleted.has(index)) {
                                                        newCompleted.delete(index);
                                                    } else {
                                                        newCompleted.add(index);
                                                    }
                                                    setCompletedSteps(newCompleted);
                                                }}
                                            >
                                                {isCompleted ? <CheckCircle2 size={20} /> : <Circle size={20} />}
                                                {isCompleted ? 'Completado' : 'Pendiente'}
                                            </button>

                                            <button
                                                className="expand-btn"
                                                onClick={() => {
                                                    const newExpanded = new Set(expandedSections);
                                                    if (newExpanded.has(index)) {
                                                        newExpanded.delete(index);
                                                    } else {
                                                        newExpanded.add(index);
                                                    }
                                                    setExpandedSections(newExpanded);
                                                }}
                                            >
                                                {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                            </button>
                                        </div>
                                    </div>

                                    {isExpanded && (
                                        <div className="card-content">
                                            <h4>Consultas Recomendadas:</h4>
                                            <div className="queries-list">
                                                {section.consultas.map((consulta, idx) => (
                                                    <div 
                                                        key={idx} 
                                                        className="query-item" 
                                                        onClick={() => handleQueryClick(consulta)}
                                                    >
                                                        <div className="query-number">{idx + 1}</div>
                                                        <p>{activeTab === 'fases' ? consulta.text : consulta}</p>
                                                        <div className="query-arrow">→</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Modal de resultados */}
            {modalIsOpen && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Consulta: {selectedQuery}</h3>
                            <button className="close-btn" onClick={closeModal}>
                                <X size={24} />
                            </button>
                        </div>
                        
                        <div className="modal-body">
                            {loading ? (
                                <div className="loading-container">
                                    <Loader2 className="animate-spin" size={40} />
                                    <p>Procesando consulta con IA...</p>
                                </div>
                            ) : (
                                <>
                                    {/* Respuesta del agente */}
                                    <div className="agent-response-section">
                                        <h4>💡 Respuesta del Agente</h4>
                                        <div className="agent-response-content">
                                            <ReactMarkdown
                                                components={{
                                                    code({ node, inline, className, children, ...props }) {
                                                        const match = /language-(\w+)/.exec(className || '');
                                                        return !inline && match ? (
                                                            <SyntaxHighlighter
                                                                style={tomorrow}
                                                                language={match[1]}
                                                                PreTag="div"
                                                                {...props}
                                                            >
                                                                {String(children).replace(/\n$/, '')}
                                                            </SyntaxHighlighter>
                                                        ) : (
                                                            <code className={className} {...props}>
                                                                {children}
                                                            </code>
                                                        );
                                                    }
                                                }}
                                            >
                                                {agentResponse}
                                            </ReactMarkdown>
                                        </div>
                                    </div>

                                    {/* Botón de visualización */}
                                    <div className="visualization-section">
                                        <button 
                                            className="visualize-btn"
                                            onClick={handleVizualization}
                                            disabled={vizLoading}
                                        >
                                            {vizLoading ? (
                                                <>
                                                    <Loader2 className="animate-spin" size={16} />
                                                    Generando visualización...
                                                </>
                                            ) : (
                                                <>
                                                    <BarChart3 size={20} />
                                                    Visualizar Datos
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    {/* Sección de visualización */}
                                    {queryData && (
                                        <div className="visualization-results">
                                            {queryData.hasError ? (
                                                <div className="error-message">
                                                    <h4>⚠️ Error en la visualización</h4>
                                                    <p>{queryData.error}</p>
                                                </div>
                                            ) : (
                                                <>
                                                    {/* Componente de visualización */}
                                                    <div className="chart-container">
                                                        <h4>📊 Visualización de Datos</h4>
                                                        <ChartWithControls
                                                            columns={queryData.columns || {}}
                                                            data={queryData.data || []}
                                                            column_types={queryData.column_types || {}}
                                                            hasError={queryData.hasError || false}
                                                            error={queryData.error || null}
                                                        />
                                                        
                                                        {/* Botón de descarga Excel */}
                                                        <div className="excel-download">
                                                            <button onClick={handleGenerateExcel} className="excel-btn">
                                                                <Download size={16} />
                                                                Descargar Reporte
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Tabla de datos */}
                                                    {queryData.data && queryData.data.length > 0 && (
                                                        <div className="data-table">
                                                            <h4>📋 Vista de Datos</h4>
                                                            <DataTable 
                                                                columns={queryData.columns || {}} 
                                                                data={queryData.data || []} 
                                                            />
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                .roadmap-container {
                    min-height: 100vh;
                    background-color: #f8fafc;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                }

                .tabs-header {
                    background: white;
                    border-bottom: 1px solid #e2e8f0;
                    padding: 0 2rem;
                }

                .tabs-nav {
                    max-width: 1200px;
                    margin: 0 auto;
                    display: flex;
                    gap: 0;
                }

                .tab-button {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 1rem 1.5rem;
                    border: none;
                    background: transparent;
                    color: #64748b;
                    cursor: pointer;
                    border-bottom: 3px solid transparent;
                    transition: all 0.3s ease;
                    font-weight: 500;
                }

                .tab-button:hover {
                    color: #059669;
                    background: #f0fdf4;
                }

                .tab-button.active {
                    color: #059669;
                    border-bottom-color: #059669;
                    background: #f0fdf4;
                }

                .main-content {
                    padding: 2rem;
                    max-width: 1200px;
                    margin: 0 auto;
                }

                .header {
                    background: white;
                    border-radius: 16px;
                    padding: 2rem;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
                    border: 1px solid #e2e8f0;
                    margin-bottom: 2rem;
                }

                .title-section h1 {
                    color: #1e293b;
                    font-size: 2.5rem;
                    font-weight: 700;
                    margin: 0 0 0.5rem 0;
                }

                .title-section p {
                    color: #64748b;
                    font-size: 1.1rem;
                    margin: 0;
                }

                .progress-section {
                    display: flex;
                    align-items: center;
                    gap: 2rem;
                    margin-top: 2rem;
                }

                .progress-stats {
                    display: flex;
                    gap: 2rem;
                }

                .stat {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .stat-number {
                    font-size: 2rem;
                    font-weight: 700;
                    color: #059669;
                }

                .stat-label {
                    font-size: 0.9rem;
                    color: #64748b;
                    text-transform: uppercase;
                    font-weight: 500;
                }

                .progress-bar {
                    flex: 1;
                    height: 8px;
                    background: #e2e8f0;
                    border-radius: 4px;
                    overflow: hidden;
                }

                .progress-fill {
                    height: 100%;
                    background: linear-gradient(90deg, #059669, #10b981);
                    transition: width 0.5s ease;
                }

                .timeline-container {
                    position: relative;
                }

                .timeline-line {
                    position: absolute;
                    left: 30px;
                    top: 0;
                    bottom: 0;
                    width: 2px;
                    background: #e2e8f0;
                    z-index: 1;
                }

                .timeline-item {
                    display: flex;
                    align-items: flex-start;
                    margin-bottom: 2rem;
                    position: relative;
                }

                .timeline-item.completed .timeline-card {
                    background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
                    border-color: #10b981;
                }

                .timeline-node {
                    width: 60px;
                    height: 60px;
                    border-radius: 50%;
                    background: #059669;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                    z-index: 2;
                    box-shadow: 0 4px 12px rgba(5, 150, 105, 0.3);
                    flex-shrink: 0;
                    margin-right: 2rem;
                }

                .timeline-card {
                    flex: 1;
                    background: white;
                    border-radius: 16px;
                    border: 1px solid #e2e8f0;
                    overflow: hidden;
                    transition: all 0.3s ease;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
                }

                .timeline-card:hover {
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                    transform: translateY(-1px);
                }

                .card-header {
                    padding: 1.5rem;
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 1rem;
                }

                .role-info {
                    flex: 1;
                }

                .role-title-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 0.5rem;
                }

                .role-info h3 {
                    font-size: 1.4rem;
                    font-weight: 600;
                    margin: 0;
                    color: #059669;
                }

                .badges {
                    display: flex;
                    gap: 0.5rem;
                }

                .priority-badge {
                    padding: 0.25rem 0.75rem;
                    color: white;
                    font-size: 0.75rem;
                    font-weight: 600;
                    border-radius: 12px;
                    text-transform: uppercase;
                }

                .priority-alta { background: #dc2626; }
                .priority-media { background: #ea580c; }
                .priority-baja { background: #16a34a; }

                .time-badge {
                    padding: 0.25rem 0.75rem;
                    background: #f1f5f9;
                    color: #475569;
                    font-size: 0.75rem;
                    font-weight: 500;
                    border-radius: 12px;
                }

                .role-description {
                    color: #64748b;
                    margin: 0.5rem 0;
                    line-height: 1.5;
                }

                .query-count {
                    color: #10b981;
                    font-size: 0.9rem;
                    font-weight: 500;
                }

                .card-actions {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                    align-items: flex-end;
                }

                .completion-btn {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem 1rem;
                    border: 2px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    font-size: 0.9rem;
                    font-weight: 500;
                }

                .completion-btn:hover {
                    border-color: #10b981;
                    color: #10b981;
                }

                .completion-btn.completed {
                    background: #10b981;
                    border-color: #10b981;
                    color: white;
                }

                .expand-btn {
                    padding: 0.5rem;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .expand-btn:hover {
                    background: #f8fafc;
                    border-color: #cbd5e1;
                }

                .card-content {
                    padding: 0 1.5rem 1.5rem;
                    border-top: 1px solid #f1f5f9;
                    animation: slideDown 0.3s ease;
                }

                .card-content h4 {
                    color: #374151;
                    margin: 1rem 0;
                    font-size: 1.1rem;
                    font-weight: 600;
                }

                .queries-list {
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                }

                .query-item {
                    display: flex;
                    align-items: flex-start;
                    gap: 1rem;
                    padding: 1rem;
                    background: #f8fafc;
                    border-radius: 8px;
                    border-left: 4px solid #10b981;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    position: relative;
                    border: 1px solid transparent;
                }

                .query-item:hover {
                    background: #f0fdf4;
                    transform: translateX(4px);
                    border-color: #10b981;
                    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);
                }

                .query-number {
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    background: #10b981;
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.8rem;
                    font-weight: 600;
                    flex-shrink: 0;
                }

                .query-item p {
                    margin: 0;
                    color: #374151;
                    line-height: 1.5;
                    flex: 1;
                }

                .query-arrow {
                    color: #10b981;
                    font-weight: bold;
                    font-size: 1.2rem;
                }

                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    backdrop-filter: blur(4px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 2rem;
                    animation: fadeIn 0.3s ease;
                }

                .modal-content {
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                    max-width: 1000px;
                    width: 100%;
                    max-height: 90vh;
                    overflow: hidden;
                    animation: slideUp 0.3s ease;
                }

                .modal-header {
                    padding: 2rem 2rem 1rem;
                    border-bottom: 1px solid #e2e8f0;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
                }

                .modal-header h3 {
                    color: #1e293b;
                    margin: 0;
                    font-size: 1.5rem;
                    font-weight: 700;
                }

                .close-btn {
                    padding: 0.5rem;
                    border: none;
                    background: rgba(255, 255, 255, 0.8);
                    color: #64748b;
                    cursor: pointer;
                    border-radius: 8px;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .close-btn:hover {
                    background: #e2e8f0;
                    color: #374151;
                    transform: scale(1.05);
                }

                .modal-body {
                    padding: 2rem;
                    max-height: calc(90vh - 120px);
                    overflow-y: auto;
                }

                .loading-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 3rem;
                    text-align: center;
                }

                .animate-spin {
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .agent-response-section {
                    background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
                    padding: 1.5rem;
                    border-radius: 12px;
                    margin-bottom: 2rem;
                    border-left: 4px solid #10b981;
                }

                .agent-response-section h4 {
                    margin: 0 0 1rem 0;
                    color: #065f46;
                    font-size: 1.1rem;
                    font-weight: 600;
                }

                .agent-response-content {
                    color: #065f46;
                    line-height: 1.6;
                }

                .visualization-section {
                    margin: 2rem 0;
                    text-align: center;
                }

                .visualize-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1.5rem;
                    background: #10b981;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    font-weight: 500;
                    transition: all 0.3s ease;
                }

                .visualize-btn:hover:not(:disabled) {
                    background: #059669;
                    transform: translateY(-1px);
                }

                .visualize-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .visualization-results {
                    margin-top: 2rem;
                    border-top: 1px solid #e2e8f0;
                    padding-top: 2rem;
                }

                .error-message {
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                    border-radius: 8px;
                    padding: 1rem;
                    color: #dc2626;
                }

                .chart-container {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 2rem;
                    margin-bottom: 2rem;
                }

                .chart-container h4 {
                    margin: 0 0 1rem 0;
                    color: #374151;
                    font-size: 1.1rem;
                    font-weight: 600;
                }

                .excel-download {
                    margin-top: 1rem;
                    text-align: center;
                }

                .excel-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem 1rem;
                    background: #16a34a;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 0.9rem;
                    transition: all 0.3s ease;
                }

                .excel-btn:hover {
                    background: #15803d;
                }

                .data-table {
                    margin-top: 2rem;
                }

                .data-table h4 {
                    margin: 0 0 1rem 0;
                    color: #374151;
                    font-size: 1.1rem;
                    font-weight: 600;
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px) scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }

                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @media (max-width: 768px) {
                    .roadmap-container {
                        padding: 0;
                    }
                    
                    .main-content {
                        padding: 1rem;
                    }
                    
                    .timeline-line {
                        left: 20px;
                    }
                    
                    .timeline-node {
                        width: 40px;
                        height: 40px;
                        margin-right: 1rem;
                    }
                    
                    .card-header {
                        flex-direction: column;
                        align-items: flex-start;
                    }
                    
                    .card-actions {
                        flex-direction: row;
                        width: 100%;
                        justify-content: space-between;
                    }
                    
                    .role-title-row {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 0.5rem;
                    }

                    .modal-overlay {
                        padding: 1rem;
                    }
                    
                    .modal-content {
                        max-height: 95vh;
                    }
                    
                    .modal-header {
                        padding: 1.5rem 1.5rem 1rem;
                    }
                    
                    .modal-body {
                        padding: 1.5rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default GuiaInteractiva;